/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button } from '@atoms';
import { ArrowTopRighticon } from '@atoms/Icons/ArrowTopRightIcon';
import { FileDocumentIcon } from '@atoms/Icons/FileDocumentIcon';
import { HelpIcon } from '@atoms/Icons/HelpIcon';
import { UserIcon } from '@atoms/Icons/UserIcon';
import { Axios } from '@helpers';
import { Box, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import { ConfirmAuthorizedModal, TakedownImpersonation } from '@organisms';
import { AssetDetailsType, errorMessage, SocialMediaLinks, successMessage } from '@services';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { useSocialUserInfo } from '../api/get-social-user-info';
import DataSocialMediaUpdateModal from './data-social-media-update-modal';

const SocialUserInfo = () => {
  const { id, assetId } = useParams();
  const { data, isLoading } = useSocialUserInfo({ id: id!, assetId: assetId! });
  const [isOpenDataSocial, setIsOpenDataSocial] = useState(false);
  const [openModalTakedown, setOpenModalTakedown] = useState<boolean>(false);
  const [openAuthorizedModal, setOpenAuthorizedModal] = useState<boolean>(false);
  const helperRef = React.useRef<HTMLButtonElement | null>(null);

  const {
    data: accountDetail,
    refetch,
    isLoading: accountLoading,
  } = useQuery<unknown, unknown, AssetDetailsType>(`asset-account-details/${id}`, () =>
    Axios.get(`/admin/accounts/${id}/models/${assetId}`).then((res) => res.data),
  );

  const { authorizeTakedownImpersonation, properties } = (accountDetail as AssetDetailsType) || {};

  const { socialMedia } = properties || {};

  const updateAuthorizeTakedownImpersonation = async (value: boolean) => {
    const response = await Axios.put(`/admin/accounts/${id}/models/${assetId}`, {
      authorizeTakedownImpersonation: value,
    });
    return response.data;
  };

  const { mutate: onUpdateAuthorizeTakedownImpersonation, isLoading: updateLoading } = useMutation({
    mutationFn: updateAuthorizeTakedownImpersonation,
    onSuccess: () => {
      refetch();
      setOpenAuthorizedModal(false);
      successMessage(`Authorization status was successfully updated.`);
    },
    onError: (error) => {
      console.error('Error creating user:', error);
      errorMessage('Failed to update Takedown Impersonation.');
    },
  });

  const handleOnClickIconOpenLink = () => {
    if (data && data.transparencyReportLink) {
      window.open(data.transparencyReportLink, '_blank');
    }
  };

  const handleClickOfficialSocialMedia = () => {
    setIsOpenDataSocial(true);
  };

  const handleOnClickTakedownImpersonation = () => {
    onUpdateAuthorizeTakedownImpersonation(!authorizeTakedownImpersonation);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        marginBottom: '24px',
      }}
    >
      <Box>
        <Typography variant="h5" mb="8px">
          Social media
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
          <FileDocumentIcon width={18} height={20} />
          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="text.primary">
              Transparency Report:{' '}
            </Typography>
            {isLoading ? (
              <CircularProgress
                sx={{
                  width: '10px !important',
                  height: '10px !important',
                  marginLeft: '10px',
                }}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1.25}>
                <Typography component="span" variant="body2" color="text.primary" fontWeight={600}>
                  &nbsp;{data?.firstName} {data?.lastName}
                </Typography>
                {data?.transparencyReportLink && (
                  <IconButton
                    onClick={handleOnClickIconOpenLink}
                    size="small"
                    sx={{ width: '16px', height: '16px' }}
                  >
                    <ArrowTopRighticon width={12} height={12} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box display={'flex'} gap={2} alignItems="center">
          <Box
            display={'flex'}
            gap={1.25}
            alignItems="center"
            sx={{
              '& .MuiSvgIcon-root': {
                fill: '#fff',
              },
            }}
          >
            <UserIcon width={24} height={24} />
            <Typography component="span" color="text.primary" variant="body2">
              Takedown Impersonation
            </Typography>
            <Box
              ref={helperRef}
              sx={{ cursor: 'pointer' }}
              onClick={() => setOpenModalTakedown(true)}
            >
              <HelpIcon
                sx={{
                  width: '16px',
                  height: '16px',
                }}
              />
            </Box>
            {updateLoading || accountLoading ? (
              <Skeleton
                variant="rectangular"
                width={110}
                height={55}
                animation="wave"
                sx={{ borderRadius: 3 }}
              />
            ) : (
              <Button
                variant="contained"
                color={authorizeTakedownImpersonation ? 'success' : 'error'}
                onClick={() => setOpenAuthorizedModal((prev) => !prev)}
                sx={{
                  width: 'fit-content',
                  paddingX: '10px',
                  borderRadius: '10px',
                  '& .MuiSvgIcon-root': {
                    fill: '#fff',
                  },
                }}
              >
                <Box component="div" display="flex" gap={1.25}>
                  <Typography component="span" color="text.white" variant="body2">
                    {authorizeTakedownImpersonation ? 'Authorized' : 'Unauthorized'}
                  </Typography>
                </Box>
              </Button>
            )}
          </Box>
          <Button
            variant="outlined"
            color="black"
            onClick={handleClickOfficialSocialMedia}
            sx={{
              width: 'fit-content',
              paddingX: '10px',
              borderRadius: '10px',
              '& .MuiSvgIcon-root': {
                fill: '#fff',
              },
            }}
          >
            <Box component="div" display="flex" gap={1.25}>
              <UserIcon width={24} height={24} />
              <Typography component="span" color="text.primary" variant="body2">
                Official Social Media
              </Typography>
            </Box>
          </Button>
        </Box>

        <DataSocialMediaUpdateModal
          isOpenDataSocial={isOpenDataSocial}
          links={socialMedia as SocialMediaLinks[]}
          loading={isLoading}
          onClose={() => setIsOpenDataSocial((prev) => !prev)}
          onReload={refetch}
        />
        <TakedownImpersonation
          open={openModalTakedown}
          onCancel={() => setOpenModalTakedown((prev) => !prev)}
        />
        <ConfirmAuthorizedModal
          open={openAuthorizedModal}
          onCancel={() => setOpenAuthorizedModal((prev) => !prev)}
          onConfirm={() => handleOnClickTakedownImpersonation()}
          authorizeTakedownImpersonation={authorizeTakedownImpersonation}
        />
      </Box>
    </Box>
  );
};
export default React.memo(SocialUserInfo);
